import { initializeDataTable } from "@/utils/tableHelper";
import { mapState } from "vuex";
import axios from "axios";
import * as XLSX from "xlsx";

export default {
    computed: {
        ...mapState({
            access_token: (state) => state.access_token,
        }),
    },

    data() {
        return {
            table: null,
            filter: {},
            isLoading: false,
            isExporting: false,
        };
    },

    props: {
        tableId: {
            type: String,
            required: true,
        },

        filterConfigs: {
            type: Array,
            default: () => [],
        },

        tableOptions: {
            type: Object,
            required: true,
        },
    },

    created() {
        this.filterConfigs.forEach((config) => {
            this.$set(
                this.filter,
                config.field,
                config.defaultValue !== undefined ? config.defaultValue : ""
            );
        });
    },

    mounted() {
        this.$nextTick(() => {
            this.initializeTable();
        });
    },

    beforeDestroy() {
        if (this.table) {
            this.table.destroy();
            this.table = null;
        }
    },

    methods: {
        buildApiUrl() {
            const baseUrl = this.tableOptions.baseUrl;
            const params = new URLSearchParams();

            Object.entries(this.filter).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== "") {
                    params.set(key, value);
                }
            });

            const query = params.toString();

            if (!query) {
                return baseUrl;
            }

            return `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${query}`;
        },

        initializeTable() {
            const element = document.getElementById(this.tableId);

            if (!element) {
                console.error(
                    `Element tabel dengan ID "${this.tableId}" tidak ditemukan.`
                );
                return;
            }

            if (this.table) {
                this.table.destroy();
                this.table = null;
            }

            this.isLoading = false;

            this.table = initializeDataTable(
                element,
                {
                    ...this.tableOptions,
                    buildApiUrl: this.buildApiUrl,
                    autoLoad: false,
                },
                this.$store
            );
        },

        async refreshTable() {
            if (this.isLoading) {
                return;
            }

            if (!this.table) {
                this.initializeTable();
            }

            if (!this.table) {
                return;
            }

            this.isLoading = true;

            try {
                if (typeof this.table.setPage === "function") {
                    try {
                        await this.table.setPage(1);
                    } catch (error) {}
                }

                await this.table.setData(this.buildApiUrl());
            } catch (error) {
                console.error("Gagal memuat data tabel:", error);

                try {
                    this.table.clearData();
                } catch (clearError) {
                    console.error("Gagal membersihkan tabel:", clearError);
                }
            } finally {
                this.isLoading = false;
            }
        },

        async resetFilters() {
            this.filterConfigs.forEach((config) => {
                this.$set(
                    this.filter,
                    config.field,
                    config.defaultValue !== undefined ? config.defaultValue : ""
                );
            });

            await this.$nextTick();

            if (this.table) {
                this.table.clearData();
            }

            this.isLoading = false;
        },

        extractRows(body, url) {
            let parsed = body;

            if (typeof this.tableOptions.ajaxResponse === "function") {
                parsed = this.tableOptions.ajaxResponse(url, {}, body);
            }

            if (Array.isArray(parsed)) {
                return parsed;
            }

            if (Array.isArray(parsed?.data)) {
                return parsed.data;
            }

            if (Array.isArray(parsed?.data?.data)) {
                return parsed.data.data;
            }

            return [];
        },

        isRawatInapExport() {
            const baseUrl = this.tableOptions.baseUrl || "";

            return baseUrl.includes(
                "/rekapitulasi-pelayanan/kegiatan-pelayanan-rawat-inap"
            );
        },

        getExportColumns() {
            const result = [];

            const flatten = (columns) => {
                columns.forEach((column) => {
                    if (column.visible === false) {
                        return;
                    }

                    if (
                        Array.isArray(column.columns) &&
                        column.columns.length > 0
                    ) {
                        flatten(column.columns);
                        return;
                    }

                    if (column.field) {
                        result.push(column);
                    }
                });
            };

            flatten(this.tableOptions.columns || []);

            return result;
        },

        countExportLeaves(columns) {
            return columns.reduce((total, column) => {
                if (column.visible === false) {
                    return total;
                }

                if (
                    Array.isArray(column.columns) &&
                    column.columns.length > 0
                ) {
                    return total + this.countExportLeaves(column.columns);
                }

                return total + (column.field ? 1 : 0);
            }, 0);
        },

        getExportHeaderLayout() {
            const columns = (this.tableOptions.columns || []).filter(
                (column) => column.visible !== false
            );

            const getDepth = (items, depth = 1) => {
                let maxDepth = depth;

                items.forEach((column) => {
                    if (
                        column.visible !== false &&
                        Array.isArray(column.columns) &&
                        column.columns.length > 0
                    ) {
                        maxDepth = Math.max(
                            maxDepth,
                            getDepth(column.columns, depth + 1)
                        );
                    }
                });

                return maxDepth;
            };

            const maxDepth = getDepth(columns);
            const leafColumns = this.getExportColumns();

            const headerRows = Array.from({ length: maxDepth }, () =>
                Array(leafColumns.length).fill("")
            );

            const merges = [];

            let currentColumn = 0;

            const processColumns = (items, depth = 0) => {
                items.forEach((column) => {
                    if (column.visible === false) {
                        return;
                    }

                    const children = Array.isArray(column.columns)
                        ? column.columns.filter(
                              (child) => child.visible !== false
                          )
                        : [];

                    if (children.length > 0) {
                        const startColumn = currentColumn;
                        const childLeafCount = this.countExportLeaves(children);

                        headerRows[depth][startColumn] = column.title || "";

                        if (childLeafCount > 1) {
                            merges.push({
                                s: {
                                    r: depth,
                                    c: startColumn,
                                },
                                e: {
                                    r: depth,
                                    c: startColumn + childLeafCount - 1,
                                },
                            });
                        }

                        processColumns(children, depth + 1);
                    } else if (column.field) {
                        headerRows[depth][currentColumn] =
                            column.title || column.field;

                        if (depth < maxDepth - 1) {
                            merges.push({
                                s: {
                                    r: depth,
                                    c: currentColumn,
                                },
                                e: {
                                    r: maxDepth - 1,
                                    c: currentColumn,
                                },
                            });
                        }

                        currentColumn++;
                    }
                });
            };

            processColumns(columns);

            return {
                headerRows,
                merges,
                leafColumns,
            };
        },

        getExportCellValue(row, column, index) {
            if (column.formatter === "rownum") {
                return index + 1;
            }

            const value = row?.[column.field];

            if (value === null || value === undefined) {
                return "";
            }

            return value;
        },

        exportStandardExcel(rows) {
            const columns = (this.tableOptions.columns || []).filter(
                (column) => column.field
            );

            const sheetRows = rows.map((row, index) => {
                const item = {};

                columns.forEach((column) => {
                    const key = column.title || column.field;

                    item[key] =
                        column.formatter === "rownum"
                            ? index + 1
                            : row[column.field] ?? "";
                });

                return item;
            });

            const worksheet = XLSX.utils.json_to_sheet(sheetRows);
            const workbook = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

            XLSX.writeFile(workbook, `Data Laporan ${this.title}.xlsx`);
        },

        exportRawatInapExcel(rows) {
            const { headerRows, merges, leafColumns } =
                this.getExportHeaderLayout();

            const sheetData = [...headerRows];

            rows.forEach((row, index) => {
                sheetData.push(
                    leafColumns.map((column) =>
                        this.getExportCellValue(row, column, index)
                    )
                );
            });

            const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

            if (merges.length > 0) {
                worksheet["!merges"] = merges;
            }

            worksheet["!cols"] = leafColumns.map((column) => ({
                wch: Math.max(
                    12,
                    Math.min(
                        35,
                        String(column.title || column.field || "").length + 5
                    )
                ),
            }));

            const workbook = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(workbook, worksheet, "Rawat Inap");

            XLSX.writeFile(
                workbook,
                `Data Laporan ${this.title || "RL 3.2 Rawat Inap"}.xlsx`
            );
        },

        async downloadExcel() {
            if (this.isExporting) {
                return;
            }

            this.isExporting = true;

            try {
                const baseUrl = this.buildApiUrl();

                const url = `${baseUrl}${
                    baseUrl.includes("?") ? "&" : "?"
                }page=1&size=-1`;

                const token =
                    this.access_token || localStorage.getItem("access_token");

                const response = await axios.get(url, {
                    headers: {
                        Authorization: "Bearer " + token,
                        Accept: "application/json",
                    },
                });

                const rows = this.extractRows(response.data, url);

                if (rows.length === 0) {
                    alert("Data kosong");
                    return;
                }

                if (this.isRawatInapExport()) {
                    this.exportRawatInapExcel(rows);
                } else {
                    this.exportStandardExcel(rows);
                }
            } catch (error) {
                console.error("Gagal mengunduh data:", error);
                alert("Gagal mengunduh data");
            } finally {
                this.isExporting = false;
            }
        },
    },
};
